/**
 * The discussion under a clip: its heading and menu, every comment with its pinned moment, the
 * trainer's own ones editable and deletable, and the composer that pins a new one to the moment on
 * screen. Typing pauses the film, Enter sends, Escape goes back to the film. A clip that is gone
 * has none of it.
 *
 * The thread is waited for, failed or answered. The composer works in all three: a comment sent
 * before the thread arrives is shown under the wait and kept through its arrival
 * (`model/threadCache`). What the trainer has in hand (a draft, an unsent comment, an edit) belongs
 * to the clip it was written on (`model/discussionState`).
 */

import { Menu } from '@base-ui/react/menu';
import { useEffect, useReducer, useRef, useState, type KeyboardEvent, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { formatClock } from '~/entities/exercise-clip';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { AvatarChip } from '~/shared/ui/AvatarChip';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import ghost from '~/shared/ui/ghostButton.module.css';
import inkButton from '~/shared/ui/inkButton.module.css';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import kbd from '~/shared/ui/kbd.module.css';
import menuStyles from '~/shared/ui/MenuSurface.module.css';
import { MoreGlyph } from '~/shared/ui/MoreGlyph';
import skel from '~/shared/ui/skeleton.module.css';
import { LinkGlyph, PinGlyph, SendGlyph } from '~/shared/ui/VideoGlyphs';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useVideoClock, type ClipPlayback, type VideoClock } from '../lib/useClipPlayback';
import type { ClipPlayerData } from '../lib/useClipPlayerData';
import { discussionReducer, initialDiscussion, unsentHere } from '../model/discussionState';
import { commentOffsetMs } from '../model/playback';
import type { PlayerCommentView } from '../model/playerView';

import styles from './ClipPlayer.module.css';

const SKELETON_COMMENTS = 2;

const keycap = `${String(kbd.kbd)} ${String(styles.kbdInk)}`;

interface PlayerDiscussionProps {
  /** The clip on screen: every write, draft and error here belongs to it. */
  readonly clipId: number;
  readonly data: ClipPlayerData;
  /** The film: its clock is what a new comment is pinned to, and a stamp seeks it. */
  readonly playback: ClipPlayback;
  readonly onStatus: (status: string) => void;
  readonly onTyping: () => void;
  readonly onBackToFilm: () => void;
  readonly composerRef: Ref<HTMLTextAreaElement>;
}

export function PlayerDiscussion({
  clipId,
  data,
  playback,
  onStatus,
  onTyping,
  onBackToFilm,
  composerRef,
}: PlayerDiscussionProps) {
  const { t } = useTranslation();
  const { discussion, viewer } = data;
  const { comments, header } = data.view;
  const gate = useSkeletonGate(discussion.pending);
  const [state, dispatch] = useReducer(discussionReducer, clipId, initialDiscussion);
  if (state.clipId !== clipId) dispatch({ kind: 'show', clipId });
  const { draft, editing, error } = state;
  const [pinned, setPinned] = useState(true);
  const [deleting, setDeleting] = useState<PlayerCommentView | null>(null);
  const [deleted, setDeleted] = useState(false);
  const thread = useRef<HTMLOListElement>(null);
  const editField = useRef<HTMLTextAreaElement>(null);

  const editingId = editing?.id ?? null;
  useEffect(() => {
    const field = editField.current;
    if (editingId === null || field === null) return;
    field.focus();
    field.setSelectionRange(field.value.length, field.value.length);
  }, [editingId]);

  const post = (body: string, offsetMs: number | null) => {
    void discussion.post(clipId, body, offsetMs).then((ok) => {
      if (!ok) dispatch({ kind: 'sendFailed', clipId, body, offsetMs });
    });
  };

  const send = () => {
    const body = draft.trim();
    if (body === '') return;
    const offset = commentOffsetMs(pinned, playback.currentTime());
    dispatch({ kind: 'send' });
    setPinned(true);
    post(body, offset);
  };

  const saveEdit = (comment: PlayerCommentView) => {
    if (editing === null || editing.saving) return;
    const body = editing.body.trim();
    if (body === '') return;
    dispatch({ kind: 'editSave' });
    void discussion.edit(clipId, comment.id, body, comment.pin?.offsetMs ?? null).then((ok) => {
      dispatch({ kind: 'editSettled', clipId, id: comment.id, ok });
    });
  };

  const onComposerKey = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onBackToFilm();
    }
  };

  /** One comment of the thread; one sent before the thread arrived offers no edit yet. */
  const commentRow = (comment: PlayerCommentView, answered: boolean) => {
    const edit = editing?.id === comment.id ? editing : null;
    return (
      <li
        className={`${String(styles.comment)} ${comment.pending ? String(styles.commentPending) : ''}`}
        key={comment.id}
      >
        <AvatarChip
          avatarUrl={comment.person.avatarUrl}
          className={styles.personComment}
          initials={comment.person.initials}
          variant="clip"
        />
        {comment.pin === null ? (
          <span className={`${String(styles.stamp)} ${String(styles.stampNone)}`}>
            <span aria-hidden="true">–</span>
          </span>
        ) : (
          <button
            aria-label={t('web:clipPlayer.discussion.seek', { time: comment.pin.stamp })}
            className={styles.stamp}
            onClick={() => {
              playback.seekTo(comment.pin?.seconds ?? 0);
            }}
            type="button"
          >
            {comment.pin.stamp}
          </button>
        )}
        <div className={styles.commentBody}>
          <div className={styles.commentHead}>
            <span className={styles.who}>{comment.person.name}</span>
            <span className={styles.when}>
              {comment.when}
              {comment.edited ? `, ${t('web:clipPlayer.discussion.edited')}` : ''}
            </span>
            <span className={styles.spacer} />
            {answered && comment.mine && !comment.pending && edit === null ? (
              <>
                <button
                  className={styles.textLink}
                  onClick={() => {
                    dispatch({ kind: 'editStart', id: comment.id, body: comment.body });
                  }}
                  type="button"
                >
                  {t('web:clipPlayer.discussion.edit')}
                </button>
                <button
                  className={styles.textLink}
                  onClick={() => {
                    setDeleted(false);
                    setDeleting(comment);
                  }}
                  type="button"
                >
                  {t('web:clipPlayer.discussion.delete')}
                </button>
              </>
            ) : null}
          </div>
          {edit === null ? (
            <p className={styles.commentText}>{comment.body}</p>
          ) : (
            <div className={styles.edit}>
              <textarea
                aria-label={t('web:clipPlayer.discussion.field')}
                className={styles.field}
                onChange={(event) => {
                  dispatch({ kind: 'editType', body: event.target.value });
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    dispatch({ kind: 'editCancel' });
                  }
                }}
                ref={editField}
                rows={3}
                value={edit.body}
              />
              <div className={styles.editActions}>
                <button
                  aria-disabled={edit.saving}
                  className={`${String(ghost.ghost)} ${String(styles.small)}`}
                  onClick={() => {
                    dispatch({ kind: 'editCancel' });
                  }}
                  type="button"
                >
                  {t('web:clipPlayer.discussion.cancel')}
                </button>
                <button
                  aria-busy={edit.saving}
                  className={`${String(inkButton.ink)} ${String(styles.small)}`}
                  onClick={() => {
                    saveEdit(comment);
                  }}
                  type="button"
                >
                  {t('web:clipPlayer.discussion.save')}
                </button>
              </div>
              {edit.failed ? (
                <p className={styles.error} role="alert">
                  {t('web:clipPlayer.discussion.saveFailed')}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </li>
    );
  };

  const counted = !gate.waiting && !discussion.failed;

  return (
    <>
      <div className={styles.discussionHead}>
        <h3 className={styles.discussionTitle}>
          {t('web:clipPlayer.discussion.title')}{' '}
          <span className={`${String(styles.count)} ${gate.reveal}`}>
            {counted ? comments.length : ''}
          </span>
        </h3>
        <Menu.Root>
          <Menu.Trigger aria-label={t('web:clipPlayer.discussion.more')} className={styles.icon}>
            <MoreGlyph className={styles.glyph} />
          </Menu.Trigger>
          <Menu.Portal>
            <Menu.Positioner align="end" className={menuStyles.positioner} sideOffset={6}>
              <Menu.Popup className={menuStyles.popup}>
                <Menu.Item
                  className={menuStyles.item}
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(window.location.href)
                      .then(() => {
                        onStatus(t('web:clipPlayer.discussion.copied'));
                      })
                      .catch(() => undefined);
                  }}
                >
                  <LinkGlyph className={styles.glyphSmall} />
                  <span className={menuStyles.itemLabel}>{t('web:clipPlayer.discussion.copyLink')}</span>
                </Menu.Item>
                {header === null || header.by.isYou ? null : (
                  <p className={styles.menuNote}>
                    {t('web:clipPlayer.discussion.onlyAuthor', { name: header.by.name })}
                  </p>
                )}
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
      </div>

      <ol
        aria-busy={gate.waiting}
        aria-label={t('web:clipPlayer.discussion.title')}
        className={`${String(styles.comments)} ${gate.reveal}`}
        ref={thread}
        tabIndex={-1}
      >
        {gate.waiting ? (
          <>
            <li className={hidden.srOnly}>{t('web:clipPlayer.states.loading')}</li>
            {gate.skeleton
              ? Array.from({ length: SKELETON_COMMENTS }, (_, index) => (
                  <li aria-hidden="true" className={styles.comment} key={index}>
                    <i
                      className={`${String(skel.bar)} ${String(skel.disc)} ${String(styles.personComment)} ${String(styles.skelPerson)}`}
                    />
                    <i className={`${String(skel.bar)} ${String(styles.skelLine)}`} />
                  </li>
                ))
              : null}
            {comments.map((comment) => commentRow(comment, false))}
          </>
        ) : discussion.failed ? (
          <li className={styles.commentsEmpty}>
            {t('web:clipPlayer.discussion.failed')}{' '}
            <button className={inlineButton.inlineTextButton} onClick={discussion.retry} type="button">
              {t('web:clipPlayer.states.retry')}
            </button>
          </li>
        ) : comments.length === 0 ? (
          <li className={styles.commentsEmpty}>{t('web:clipPlayer.discussion.empty')}</li>
        ) : (
          comments.map((comment) => commentRow(comment, true))
        )}
      </ol>

      <div className={styles.composer}>
        {unsentHere(state).map((entry) => (
          <div className={styles.unsent} key={entry.key} role="alert">
            <span className={styles.unsentText}>
              <b>{t('web:clipPlayer.discussion.notSent')}</b> {entry.body}
            </span>
            <button
              className={inlineButton.inlineTextButton}
              onClick={() => {
                dispatch({ kind: 'resend', key: entry.key });
                post(entry.body, entry.offsetMs);
              }}
              type="button"
            >
              {t('web:clipPlayer.discussion.resend')}
            </button>
            <button
              className={inlineButton.inlineTextButton}
              onClick={() => {
                dispatch({ kind: 'discard', key: entry.key });
              }}
              type="button"
            >
              {t('web:clipPlayer.discussion.discard')}
            </button>
          </div>
        ))}
        <div className={styles.pinRow}>
          {pinned ? (
            <ComposerPin
              clock={playback.clock}
              onUnpin={() => {
                setPinned(false);
              }}
            />
          ) : (
            <button
              className={styles.chip}
              onClick={() => {
                setPinned(true);
              }}
              type="button"
            >
              <PinGlyph className={styles.glyphSmall} />
              {t('web:clipPlayer.discussion.repin')}
            </button>
          )}
        </div>
        <div className={styles.fieldRow}>
          <AvatarChip
            avatarUrl={viewer.avatarUrl}
            className={styles.personComment}
            initials={viewer.initials}
            variant="clip"
          />
          <textarea
            aria-label={t('web:clipPlayer.discussion.field')}
            className={styles.field}
            onChange={(event) => {
              dispatch({ kind: 'type', text: event.target.value });
            }}
            onFocus={onTyping}
            onKeyDown={onComposerKey}
            placeholder={t('web:clipPlayer.discussion.placeholder')}
            ref={composerRef}
            rows={2}
            value={draft}
          />
        </div>
        <div className={styles.composerFoot}>
          <span className={styles.composerHint}>
            {t('web:clipPlayer.discussion.hintWrite')} <kbd className={keycap}>Enter</kbd>{' '}
            {t('web:clipPlayer.discussion.hintSend')} <kbd className={keycap}>Esc</kbd>{' '}
            {t('web:clipPlayer.discussion.hintBack')}
          </span>
          <button
            className={`${String(inkButton.ink)} ${String(styles.send)}`}
            onClick={send}
            type="button"
          >
            <SendGlyph className={styles.glyphSmall} />
            {t('web:clipPlayer.discussion.send')}
          </button>
        </div>
        {error === null ? null : (
          <p className={styles.error} role="alert">
            {t(`web:clipPlayer.discussion.${error}`)}
          </p>
        )}
      </div>

      <ConfirmDialog
        finalFocus={deleted ? thread : undefined}
        onCancel={() => {
          setDeleting(null);
        }}
        onConfirm={() => {
          // The row that opened this is about to go; focus lands on the thread instead.
          setDeleted(true);
          setDeleting(null);
          if (deleting === null) return;
          void discussion.remove(clipId, deleting.id).then((ok) => {
            if (!ok) dispatch({ kind: 'deleteFailed', clipId });
          });
        }}
        prompt={
          deleting === null
            ? null
            : {
                title: t('web:clipPlayer.discussion.deleteConfirm'),
                description: deleting.body,
                confirmLabel: t('web:clipPlayer.discussion.delete'),
                cancelLabel: t('web:clipPlayer.discussion.cancel'),
              }
        }
      />
    </>
  );
}

/** The moment a new comment will be pinned to. It reads the clock itself, so nothing above does. */
function ComposerPin({
  clock,
  onUnpin,
}: {
  readonly clock: VideoClock | null;
  readonly onUnpin: () => void;
}) {
  const { t, i18n } = useTranslation();
  const time = useVideoClock(clock, (seconds) => formatClock(seconds, true, i18n.language));
  return (
    <>
      <button
        aria-label={t('web:clipPlayer.discussion.unpin', { time })}
        className={styles.stamp}
        onClick={onUnpin}
        type="button"
      >
        <PinGlyph className={styles.glyphSmall} />
        {time}
        <CloseGlyph className={styles.glyphSmall} />
      </button>
      <span className={styles.pinHint}>{t('web:clipPlayer.discussion.pinHint')}</span>
    </>
  );
}
